import React, { useState } from 'react'
import cn from 'classnames'
import loadable from '@loadable/component'
import LifesumLogo from '!!@svgr/webpack!media/logo.svg'
import LifesumLogoCircle from '!!@svgr/webpack!media/logo-short.svg'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import useLocale from '../../hooks/useLocale'
import { FormattedMessage } from 'react-intl'
import {
  logOut,
  selectIsLoggedIn,
  selectIsPremium,
} from '../../store/userSlice'
import { trackStartRegistration } from '../../analytics'
import { selectPricingConfig } from '../../store/pricingSlice'
import { localizeLink } from '../../utils'
import LanguageSwitcher from './LanguageSwitcher'
import useBreakpoints from '../../hooks/useBreakpoints'
import useScroll from 'js/hooks/useScroll'

const LoginOverlay = loadable(() => import('../Login/LoginOverlay'))

export const linkClasses =
  'type-14-demibold md:type-14-demibold-caps xl:type-17-demibold-caps text-inherit mx-6 lg:mx-12 py-2 border-b-2 border-transparent hover:border-Brand transition'

type WrapperProps = {
  children: React.ReactNode
  sticky: boolean
  className?: string
  inverted: boolean
  isStuck: boolean
  fixedPosition: boolean
}

const Wrapper = ({
  children,
  sticky,
  isStuck,
  fixedPosition,
  inverted,
  className,
}: WrapperProps) => (
  <header
    role="banner"
    className={cn(
      'pt-12 pb-6 md:pb-12 lg:py-20 xl:py-30',
      'transition z-30',
      sticky && fixedPosition ? 'fixed' : 'sticky',
      sticky && 'top-0 left-0 right-0',
      isStuck && 'bg-white shadow-elevated',
      inverted && !isStuck ? 'text-TypeConstant' : 'text-Type',
      className
    )}
  >
    <div className="max-w-1680 mx-auto px-16 md:px-24 xl:px-64 xxl:px-128">
      {children}
    </div>
  </header>
)

type LogoProps = {
  inverted: boolean
  className?: string
  isStuck: boolean
  logoOnly: boolean
}

const Logo = ({ inverted, isStuck, logoOnly, className }: LogoProps) => {
  const isLoggedIn = useSelector(selectIsLoggedIn)
  const isPremium = useSelector(selectIsPremium)
  const { language } = useLocale()
  const { MD } = useBreakpoints()
  const useCircleLogo = !MD && isLoggedIn && !isPremium
  const Logo = useCircleLogo ? LifesumLogoCircle : LifesumLogo

  return (
    <Link
      data-uid="home_header_logo"
      to={`/${language !== 'en' ? language : ''}`}
      className={cn(
        className,
        'xl:-mt-4',
        !logoOnly
          ? 'flex lg:block items-center mr-auto lg:mr-24 xl:mr-40'
          : 'block'
      )}
    >
      <span className="sr-only">Lifesum.com</span>
      <Logo
        className={cn(
          'c-logo',
          useCircleLogo && 'c-logo--short',
          inverted && !isStuck ? 'text-TypeConstant' : 'text-Brand'
        )}
        id="logo"
      />
    </Link>
  )
}

type ListItemProps = {
  children: React.ReactNode
  className?: string
}

const ListItem = ({ children, className }: ListItemProps) => (
  <li className={className}>{children}</li>
)

type SignUpOrUpgradeLinkProps = {
  inverted: boolean
  isStuck: boolean
  className?: string
}

const SignUpOrUpgradeLink = ({
  className,
  inverted,
  isStuck,
}: SignUpOrUpgradeLinkProps) => {
  const isLoggedIn = useSelector(selectIsLoggedIn)
  const isPremium = useSelector(selectIsPremium)
  const pricingConfig = useSelector(selectPricingConfig())
  const { language } = useLocale()

  if (isPremium) return null

  return (
    <Link
      className={cn(
        'type-14-demibold xl:type-17-demibold',
        'block transition rounded-full',
        'bg-transparent border-2',
        'lg:ml-12 py-4 px-12 xl:px-20 xl:pt-6',
        'text-center max-w-golden-ratio lg:max-w-none truncate',
        'lg:relative',
        className,
        inverted && !isStuck
          ? 'border-TypeConstant text-TypeConstant hover:text-Type hover:bg-TypeConstant'
          : 'border-Brand text-BrandDark hover:text-TypeConstant hover:bg-Brand'
      )}
      style={{ bottom: 2 }}
      data-uid="signup_header_button"
      to={localizeLink(isLoggedIn ? '/upgrade/' : '/signup/', language)}
      onClick={() => {
        if (!isLoggedIn) {
          trackStartRegistration()
        }
      }}
    >
      {isLoggedIn ? (
        <FormattedMessage
          id="Account.upgradeToPremium"
          defaultMessage="Upgrade to Premium"
        />
      ) : (
        <>
          {pricingConfig?.trialDuration > 0 ? (
            <FormattedMessage
              id="trial_tryForFree"
              defaultMessage="Try for free"
            />
          ) : (
            <FormattedMessage
              id="Header.menuBar.signUp"
              defaultMessage="Sign up"
            />
          )}
        </>
      )}
    </Link>
  )
}

const AccountLink = () => {
  const { language } = useLocale()

  return (
    <Link
      data-uid="account_header_button"
      to={localizeLink('/account/', language)}
      className={linkClasses}
    >
      <FormattedMessage id="Header.menuBar.account" defaultMessage="Account" />
    </Link>
  )
}

const LogOutButton = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  return (
    <button
      data-uid="logout_header_button"
      onClick={(e) => {
        e.preventDefault()
        dispatch(logOut())
        navigate('/')
      }}
      style={{ maxWidth: 75 }}
      className={cn(linkClasses, 'sm:max-w-none truncate')}
    >
      <FormattedMessage id="Header.menuBar.logOut" defaultMessage="Log out" />
    </button>
  )
}

type Props = {
  sticky?: boolean
  logoOnly?: boolean
  inverted?: boolean
  fixedPosition?: boolean
}

export default function Header({
  sticky = true,
  inverted = true,
  logoOnly = false,
  fixedPosition = false,
}: Props) {
  const [showLogin, setShowLogin] = useState(false)
  const { language } = useLocale()
  const isLoggedIn = useSelector(selectIsLoggedIn)
  const { y: yScroll } = useScroll()
  const isStuck = sticky && yScroll > 0

  if (logoOnly)
    return (
      <Wrapper
        className="text-center"
        fixedPosition={fixedPosition}
        inverted={inverted}
        isStuck={isStuck}
        sticky={sticky}
      >
        <Logo isStuck={isStuck} inverted={inverted} logoOnly={logoOnly} />
      </Wrapper>
    )

  return (
    <>
      <Wrapper
        fixedPosition={fixedPosition}
        inverted={inverted}
        isStuck={isStuck}
        sticky={sticky}
      >
        <div className="lg:flex items-center">
          <div className="flex items-center lg:block">
            <Logo logoOnly={logoOnly} inverted={inverted} isStuck={isStuck} />
            <SignUpOrUpgradeLink
              inverted={inverted}
              className="lg:hidden"
              isStuck={isStuck}
            />
            <div className="ml-4 -mr-6 lg:hidden">
              <LanguageSwitcher inverted={inverted} isStuck={isStuck} />
            </div>
          </div>
          <nav className="flex-grow">
            <ul
              className={cn(
                'flex items-center',
                inverted && !isStuck ? 'text-TypeConstant' : 'text-Type',
                'mt-16 md:mt-24 lg:mt-0 -mx-6 lg:mx-0'
              )}
            >
              <ListItem>
                <Link
                  to={localizeLink('/features/', language)}
                  className={linkClasses}
                >
                  <FormattedMessage
                    id="Header.menuBar.features"
                    defaultMessage="Features"
                  />
                </Link>
              </ListItem>
              <ListItem className="mr-auto">
                <Link to="/nutrition-explained/" className={linkClasses}>
                  <FormattedMessage
                    id="Header.menuBar.articles"
                    defaultMessage="Articles"
                  />
                </Link>
              </ListItem>
              <ListItem className="hidden lg:block">
                <LanguageSwitcher inverted={inverted} isStuck={isStuck} />
              </ListItem>
              {isLoggedIn ? (
                <>
                  <ListItem>
                    <AccountLink />
                  </ListItem>
                  <ListItem>
                    <LogOutButton />
                  </ListItem>
                </>
              ) : (
                !isLoggedIn && (
                  <ListItem>
                    <button
                      type="button"
                      data-uid="login_header_button"
                      onClick={() => setShowLogin(true)}
                      className={linkClasses}
                    >
                      <FormattedMessage
                        id="Header.menuBar.logIn"
                        defaultMessage="Log in"
                      />
                    </button>
                  </ListItem>
                )
              )}
              <li className="hidden lg:block">
                <SignUpOrUpgradeLink isStuck={isStuck} inverted={inverted} />
              </li>
            </ul>
          </nav>
        </div>
      </Wrapper>
      {showLogin && <LoginOverlay onClose={() => setShowLogin(false)} />}
    </>
  )
}
